import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Stat, Badge } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

interface Conv {
  conversation_id: string;
  ai_status: string;
  last_inbound_at: string | null;
}
interface CommentAnalytics {
  since_days: number;
  stats: { total: number; dm_sent: number; ignored: number; spam: number; errors: number };
}
interface CreditsBalance { base_used: number; total_remaining: number; }

export function Analytics() {
  const api = useApi();
  const { t } = useLang();
  const { data: convs } = useQuery({
    queryKey: ['analytics-convs'],
    queryFn: () => api.get<{ items: Conv[] }>('/conversations?limit=200'),
  });
  const { data: comments } = useQuery({
    queryKey: ['analytics-comments'],
    queryFn: () => api.get<CommentAnalytics>('/social-posts/analytics'),
  });
  const { data: credits } = useQuery({
    queryKey: ['analytics-credits'],
    queryFn: () => api.get<CreditsBalance>('/credits'),
  });

  const now = Date.now();
  const week = 7 * 24 * 60 * 60 * 1000;
  const items = convs?.items ?? [];
  const lastWeek = items.filter((c) => c.last_inbound_at && now - new Date(c.last_inbound_at).getTime() < week);
  const aiHandled = items.filter((c) => c.ai_status === 'active').length;
  const escalations = items.filter((c) => c.ai_status === 'paused_auto').length;
  const aiPct = items.length > 0 ? Math.round((aiHandled / items.length) * 100) : 0;

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.analytics.title')} sub={t('page.analytics.sub')} />

      <div className="pgrid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        <Stat icon="inbox"     label={t('page.analytics.stat.convs7d')}  value={lastWeek.length.toLocaleString('en-US')} />
        <Stat icon="sparkles"  label={t('page.analytics.stat.aiPct')}    value={`${aiPct}%`} />
        <Stat icon="megaphone" label={t('page.analytics.stat.comments')} value={(comments?.stats.total ?? 0).toLocaleString('en-US')} />
        <Stat icon="send"      label={t('page.analytics.stat.dms')}      value={(comments?.stats.dm_sent ?? 0).toLocaleString('en-US')} />
        <Stat icon="bolt"      label={t('page.analytics.stat.credits')}  value={(credits?.base_used ?? 0).toLocaleString('en-US')} />
        <Stat icon="users"     label={t('page.analytics.stat.esc')}      value={escalations.toLocaleString('en-US')} />
      </div>

      <div className="card card--pad">
        <div className="flex aic gap8" style={{ marginBottom: 12 }}>
          <Icon name="chart" />
          <div style={{ fontWeight: 800, fontSize: 15 }}>{t('page.analytics.summary.h')}</div>
        </div>
        <div className="grid2" style={{ gap: 14 }}>
          <SummaryRow icon="checkcircle" kind="ok"     label={t('page.analytics.summary.ai')}  value={aiHandled} />
          <SummaryRow icon="bell"        kind="warn"   label={t('page.analytics.summary.esc')} value={escalations} />
          <SummaryRow icon="shield"      kind="muted"  label={t('page.analytics.summary.spam')} value={comments?.stats.ignored ?? 0} />
          <SummaryRow icon="trash"       kind="danger" label={t('page.analytics.summary.del')}  value={comments?.stats.spam ?? 0} />
        </div>
      </div>

      <div className="card card--pad" style={{ background: 'var(--bg-3)' }}>
        <div className="flex aic gap8" style={{ marginBottom: 6 }}>
          <Icon name="chart" style={{ color: 'var(--accent)' }} />
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t('page.analytics.more.h')}</div>
        </div>
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.7 }}>{t('page.analytics.more.p')}</div>
      </div>
    </div>
  );
}

function SummaryRow({ icon, kind, label, value }: { icon: string; kind: 'ok' | 'warn' | 'danger' | 'muted'; label: string; value: number }) {
  return (
    <div className="between" style={{ padding: '12px 14px', background: 'var(--bg-3)', border: '1px solid var(--line)', borderRadius: 12 }}>
      <div className="flex aic gap10">
        <Badge kind={kind} plain icon={icon}>{value.toLocaleString('en-US')}</Badge>
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{label}</span>
      </div>
    </div>
  );
}
